/**
 * MSW Overrides: Quotation Ancillaries scenario
 *
 * Intercepts the POST quotation endpoint and injects ancillaries into the
 * `pricing.summary.breakdown.ancillaries` field of the response.
 *
 * The logic mirrors what the backend will eventually do:
 * - Always includes subscription-benefit items (price 0.00) from the availability mock.
 * - Appends any purchasable items the user selected (codes from the request body).
 *
 * Enable by setting ENABLE_MSW=true and NUXT_PUBLIC_MSW_OVERRIDES_QUOTATION_ANCILLARIES=true
 * in your .env, then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse, bypass } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern } from '@cvo/domain';
import type { QuotationResponse, AncillaryBreakdownItem } from '@cvo/domain';
import { MOCK_BOOKING_AVAILABLE_ANCILLARIES } from './booking-ancillaries';

interface RequestAncillaryItem {
  code: string;
}

interface RequestPassengerAncillaries {
  person_id: string;
  outbound: RequestAncillaryItem[];
  inbound: RequestAncillaryItem[];
}

const PRICE_FRACTION_DIGITS = 2;

/**
 * Builds a map of ancillary code → combined price (outbound + inbound) per passenger.
 * Since outbound and inbound are always selected as a bundle, the quotation item
 * price represents the total for both legs.
 */
function buildPriceMap(): Map<string, { amount: string; currency: string }> {
  const totals = new Map<string, { amount: number; currency: string }>();

  for (const passenger of MOCK_BOOKING_AVAILABLE_ANCILLARIES.ancillaries) {
    for (const item of [...passenger.outbound, ...(passenger.inbound ?? [])]) {
      const existing = totals.get(item.code);
      const legAmount = parseFloat(item.price.amount);
      if (existing) {
        existing.amount += legAmount;
      } else {
        totals.set(item.code, { amount: legAmount, currency: item.price.currency });
      }
    }
  }

  const map = new Map<string, { amount: string; currency: string }>();
  for (const [code, value] of totals.entries()) {
    map.set(code, { amount: value.amount.toFixed(PRICE_FRACTION_DIGITS), currency: value.currency });
  }
  return map;
}

function buildIncludedItems(priceMap: Map<string, { amount: string; currency: string }>): AncillaryBreakdownItem[] {
  const items: AncillaryBreakdownItem[] = [];
  for (const [code, price] of priceMap.entries()) {
    if (parseFloat(price.amount) === 0) {
      items.push({ type: code, quantity: 1, price });
    }
  }
  return items;
}

function collectSelectedCodes(requestAncillaries: RequestPassengerAncillaries[]): Set<string> {
  const codes = new Set<string>();
  for (const passenger of requestAncillaries) {
    for (const item of [...passenger.outbound, ...passenger.inbound]) {
      codes.add(item.code);
    }
  }
  return codes;
}

function buildPaidItems(
  selectedCodes: Set<string>,
  priceMap: Map<string, { amount: string; currency: string }>,
): AncillaryBreakdownItem[] {
  const items: AncillaryBreakdownItem[] = [];
  for (const code of selectedCodes) {
    const price = priceMap.get(code);
    if (!price || parseFloat(price.amount) === 0) continue;
    items.push({ type: code, quantity: 1, price });
  }
  return items;
}

/**
 * Returns all AncillaryBreakdownItems to inject into the quotation breakdown:
 * - Included items (price 0.00) are always present.
 * - Selected paid items are appended based on the request body.
 */
function buildAncillaryItems(requestAncillaries: RequestPassengerAncillaries[] | undefined): AncillaryBreakdownItem[] {
  const priceMap = buildPriceMap();
  const included = buildIncludedItems(priceMap);

  if (!requestAncillaries?.length) return included;

  const selectedCodes = collectSelectedCodes(requestAncillaries);
  return [...included, ...buildPaidItems(selectedCodes, priceMap)];
}

export function createQuotationAncillariesOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    http.post(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.QUOTATION)}`,
      async ({ request }) => {
        const requestClone = request.clone();
        const body = await request.json() as { ancillaries?: RequestPassengerAncillaries[] };
        const originalResponse = await fetch(bypass(requestClone));
        const data = await originalResponse.json() as QuotationResponse | undefined;

        if (!originalResponse.ok || !data?.pricing?.summary) {
          return HttpResponse.json(data, { status: originalResponse.status });
        }

        const ancillaryItems = buildAncillaryItems(body.ancillaries);

        if (!ancillaryItems.length) return HttpResponse.json(data);

        const totalAmount = ancillaryItems
          .reduce((sum, item) => sum + parseFloat(item.price.amount), 0)
          .toFixed(PRICE_FRACTION_DIGITS);

        const currency = ancillaryItems[0].price.currency;

        const result: QuotationResponse = {
          ...data,
          pricing: {
            ...data.pricing,
            summary: {
              ...data.pricing.summary,
              breakdown: {
                ...data.pricing.summary.breakdown,
                ancillaries: {
                  total: { amount: totalAmount, currency },
                  items: ancillaryItems,
                },
              },
            },
          },
        };

        console.log('[MSW] quotation ancillaries injected:', result.pricing.summary.breakdown.ancillaries);

        return HttpResponse.json(result);
      },
    ),
  ];
}
